import type { PlantWeatherPoint, StatPeriod } from '@/shared/data-types';
import { slotWeatherKey, type IrradianceSlot } from './irradiance';

/**
 * 발전소 기상(`detail/ranged`) → 슬롯 순서 매핑. 발전량 슬롯과 **시각/일자 키로 매칭**한다(배열 index 매칭은
 * 결측 구간에서 통째로 밀린다 — `mapIrradianceToSlots` 와 같은 이유). 슬롯 쪽 키는 일사량과 **같은 함수**
 * (`slotWeatherKey`)를 써서 한 표 안 두 기상 소스가 어긋나지 않게 한다.
 *
 * 응답 쪽 키는 wire `date` 포맷이 확정되지 않아(`yyyyMMddHH`·`yyyy-MM-dd HH:mm`·`yyyyMM` 등) 숫자만 추출한 뒤
 * **period 와 길이로 해석**한다. [TODO:be-verify] `detail/ranged` 각 date_type 응답의 date 포맷.
 */
const digits = (value: string | null): string => (value ?? '').replace(/\D/g, '');

/** 응답 point → 매칭 키(period 별). 산출 불가면 null(스킵). */
function pointKey(period: StatPeriod, point: PlantWeatherPoint): string | null {
  const d = digits(point.date);
  if (period === 'day') {
    // 시(hour) 2자리 — 날짜가 앞에 붙어 오면 9~10번째 자리가 시, 시각만 오면 앞 2자리.
    return (d.length > 8 ? d.slice(8, 10) : d.slice(0, 2)) || null;
  }
  if (period === 'year') {
    // 월 2자리 — yyyyMM 이상이면 5~6번째 자리, 월만 오면 앞 2자리.
    return (d.length >= 6 ? d.slice(4, 6) : d.slice(0, 2)) || null;
  }
  const ymd = d.slice(0, 8);
  return ymd.length === 8 ? ymd : null;
}

/**
 * 시각 키 `HH` → 한 시간 뒤 `HH+1`(day 한정). 23시 다음은 이 날 슬롯에 자리가 없어 null.
 * 일사량이 **직전 1시간 누적**이라 `[HH, HH+1)` 구간 몫은 `HH+1` 항목에 들어 있다(아래 주석 참조).
 */
function nextHourKey(key: string): string | null {
  const hour = Number(key) + 1;
  if (!Number.isFinite(hour) || hour > 23) return null;
  return String(hour).padStart(2, '0');
}

/**
 * 슬롯 순서에 맞춘 기상 배열(결측 null). 단일 발전소 조회에서만 의미(전체는 발전소가 하나로 안 정해진다).
 *
 * **day 는 한 칸 뒤 항목에서 가져온다(행 전체)** — 일사량이 그 정시까지의 *직전 1시간 누적*이라 `[HH, HH+1)`
 * 구간 몫이 `HH+1` 항목에 들어 있다(차트 `CLIMATE_HOUR_SHIFT` 와 같은 규약, 부호만 반대 — 여기선 슬롯에서
 * 응답을 찾아가므로 +1). 날씨·기온·습도까지 **같은 행으로 함께 당긴다** — 한 행 안에서 필드마다 다른 시각을
 * 섞지 않는다(결정: 표 전체를 발전량 구간에 맞춘다). 일(day) 이상 단위는 이 규약이 없어 무보정.
 *
 * ⚠️ 이 API(`detail/ranged`)의 시간 규약은 미확인이다. [TODO:be-verify] 실 응답으로 확정 — 만약 이쪽이
 * 구간 몫(`[HH, HH+1)`)으로 온다면 이 보정과 차트의 `CLIMATE_HOUR_SHIFT` 를 함께 걷어내야 한다.
 */
export function mapPlantWeatherToSlots(
  points: PlantWeatherPoint[],
  slots: IrradianceSlot[],
  period: StatPeriod,
): Array<PlantWeatherPoint | null> {
  const byKey = new Map<string, PlantWeatherPoint>();
  for (const point of points) {
    const key = pointKey(period, point);
    if (key) byKey.set(key, point);
  }
  return slots.map((slot) => {
    const key = slotWeatherKey(period, slot);
    if (period !== 'day') return byKey.get(key) ?? null;
    const shiftedKey = nextHourKey(key);
    return (shiftedKey ? byKey.get(shiftedKey) : undefined) ?? null;
  });
}
